import { bookActions } from '@/actions';
import BottomCard from '@/components/elements/Card/Card';
import { Card } from '@/components/elements/cards';
import { LinkButton } from '@/components/elements/forms/buttons';
import Header from '@/components/elements/Header/Header';
import { classnames, getTotalPrice, shouldShowFrom, showPrices } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { BookState } from '@/types/state/book';
import { PlaceEmployee } from '@/types/state/shared';
import { Fragment, useContext, useMemo } from 'react';
import Modal from '../../redesign/Modal/Modal';
import {
  getPickedEmployee,
  getRecommendedServices,
  hasMultipleEmployees,
  ScrollToTopElement,
} from '../BookAppointment.helpers';
import { BookAppointmentContext, useBookAppointmentContext } from '../BookAppointment.hooks';
import { ANY_AMPLOYEE } from '../BookAppointments.constants';
import ServiceItem from '../shared/ServiceItem';
import Summary, { SummaryProps } from '../shared/Summary';

const baseTranslationKey = 'components.modules.modals.BookAppointment.views.SummaryView';

const SummaryView = () => {
  const { isMobileView } = useMobileView();
  const dispatch = useAppDispatch();
  const context = useBookAppointmentContext();
  const { employee, place, services: pickedServices, time } = useAppSelector((state) => state.book) as BookState;

  const recommendedServices = useMemo(
    () => getRecommendedServices(pickedServices || [], getPickedEmployee(employee, place.employees) ?? ANY_AMPLOYEE),
    [pickedServices, employee],
  );

  const pickedEmployee: PlaceEmployee | undefined = (() => {
    if (place.about?.settings?.hideEmployees) {
      return undefined;
    }

    const picked = getPickedEmployee(employee, place.employees);
    return picked ?? ANY_AMPLOYEE;
  })();

  const [pickedAddonServices, pickedMainServices] = [
    (pickedServices || []).filter((service) => service?.about?.settings?.isAddOn),
    (pickedServices || []).filter((service) => !service?.about?.settings?.isAddOn),
  ];

  const canPickRecommendedServices = recommendedServices.length > 0 && pickedMainServices.length <= 1;

  const summaryProps: SummaryProps = {
    pickedEmployee,
    pickedServices,
    showPickedEmployee: hasMultipleEmployees(place) && !Boolean(place.about?.settings?.hideEmployees),
    showAddAdditionalService: !pickedAddonServices.length,
    onRemoveService: handleRemoveService,
    onRemoveEmployee: handleRemoveEmployee,
    onShowAvailableServices: handleShowAvailableServices,
  };

  function handleShowAvailableServices() {
    context.updateView('pickServices');
  }

  function handleRemoveService(serviceId: number) {
    // @ts-ignore
    dispatch(bookActions.popService(serviceId, time?.timestamp, pickedServices, employee, place, true));

    const isRecommended = pickedServices.some((service) => service.about.settings.isAddOn);

    if (isRecommended) {
      const serviceIds = pickedAddonServices.filter((s) => s.id !== serviceId).map((s) => s.id);
      context.updateUrlState({ type: 'addOnServices', serviceIds: serviceIds.length ? serviceIds : undefined });
    } else {
      const serviceIds = pickedMainServices.filter((s) => s.id !== serviceId).map((s) => s.id);
      context.updateUrlState({ type: 'mainServices', serviceIds: serviceIds.length ? serviceIds : undefined });
    }
  }

  function handleRemoveEmployee() {
    dispatch(bookActions.removeEmployee());
    context.updateUrlState({ type: 'employee', externalId: undefined });
    context.updateView('pickEmployee');
  }

  function handleGoBack() {
    if (pickedEmployee) {
      const view = context.view.previous
        ? context.view.previous
        : summaryProps.showPickedEmployee
        ? 'pickEmployee'
        : undefined;

      if (!view) {
        context.close();
        return;
      }

      context.updateView(view);
      return;
    }

    context.view.previous ? context.updateView(context.view.previous) : context.close();
  }

  return (
    <Modal.Content style={isMobileView ? {} : { padding: 0 }} floating={!isMobileView} gradientBackground>
      <ScrollToTopElement />
      {isMobileView && <Modal.Header title={_s(`${baseTranslationKey}.title`)} onBack={handleGoBack} />}
      <div className={classnames(!isMobileView && 'p-lg space-y-lg')}>
        {!isMobileView && <Modal.Header title={_s(`${baseTranslationKey}.title`)} onBack={handleGoBack} />}
        <div className={`flex flex-col ${isMobileView ? 'px-lg' : ''}`}>
          {!isMobileView && (
            <div className="px-md">
              <Summary {...summaryProps} />
            </div>
          )}
          {isMobileView && (
            <Card className="p-lg !m-0 rounded-lg !border-0 bg-white">
              <Summary {...summaryProps} />
            </Card>
          )}
          {canPickRecommendedServices && (
            <>
              <div className="pt-xxl">
                <Header label={_s(`${baseTranslationKey}.addons.header`)} size="md" />
              </div>
              {recommendedServices.map((service, idx) => (
                <ServiceItem
                  key={service.id}
                  service={service}
                  isRecommended={true}
                  underline={recommendedServices.length - 1 !== idx}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <Fragment>
        {/* spacer to avoid elements going under fixed bottom card */}
        {isMobileView && <div className="h-[128px]" />}
        {/* spacer to avoid elements going under fixed bottom card */}
        <div className={classnames(isMobileView ? 'fixed left-0 right-0 mt-auto' : 'sticky', 'bottom-0')}>
          <BottomCard className="pb-lg h-auto w-full bg-white">
            <Footer />
          </BottomCard>
        </div>
      </Fragment>
    </Modal.Content>
  );
};

const Footer = () => {
  const props = useContext(BookAppointmentContext);
  const { employee, place, services: pickedServices, campaigns } = useAppSelector((state) => state.book) as BookState;

  const pickedEmployee = getPickedEmployee(employee, place.employees);
  const serviceCountText = _s(`${baseTranslationKey}.cta.description`, { count: pickedServices.length });
  const priceListId = pickedEmployee?.about?.priceListId ?? 0;
  const total = getTotalPrice(pickedServices, place, priceListId, {}, 0, 1, true, campaigns);
  const totalWithoutCampaigns = getTotalPrice(pickedServices, place, priceListId);
  const hasCampaignPrice = total !== totalWithoutCampaigns;
  const isPriceVariable =
    shouldShowFrom(pickedServices, place) && total !== _s('variablePrice') && total !== _s('freePrice');
  const shouldShowPrices = showPrices(place, pickedServices);
  const shouldShowCTA = pickedServices.length > 0;
  const calendarPathname = (() => {
    if (!shouldShowCTA) return '';
    return `/boka-tjanst/${place.about.slug ? place.about.slug + '-' : ''}${place.id}/${pickedServices[0].about.slug}-${
      pickedServices[0].id
    } `;
  })();

  return (
    <Fragment>
      {shouldShowPrices && (
        <div className="pb-sm flex items-center justify-between">
          <span className="text-black-900 text-m font-semibold">{serviceCountText}</span>
          <span className="text-black-900 text-m font-semibold">
            {isPriceVariable ? `${_s('from')} ` : ''}
            {hasCampaignPrice ? (
              <span className="text-highlight-700">
                {total} <span className="text-black-600 line-through">({totalWithoutCampaigns})</span>
              </span>
            ) : (
              total
            )}
          </span>
        </div>
      )}
      <LinkButton
        data-cy="goToWeekPickerButton"
        onClick={props.close}
        variant="primary"
        size="md"
        to={calendarPathname}>
        {_s(`${baseTranslationKey}.cta.label`)}
      </LinkButton>
    </Fragment>
  );
};

export default SummaryView;
